import React, { useEffect, useRef } from 'react';
import Utilities from '@util/Utilities';
import CalendarDay from '@app/state/calendar/CalendarDay';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import Spinner from '@app/component/static/Spinner';
import MarketStateDailySnapshotState
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/MarketStateDailySnapshotState';
import {
	useLatestProcessRequestExecutionStateMonitor,
} from '@app/site/secured/rateshopper/rates/provider/useLatestProcessRequestExecutionStateMonitor';
import Process from '@app/model/process-request/Process';


const EmptyCalendarDayFragment: React.FunctionComponent<{ marketStateDailySnapshotState: MarketStateDailySnapshotState, selectedCalendarDay: CalendarDay | undefined, index: number }> = ({ marketStateDailySnapshotState, selectedCalendarDay, index }) => {
	const div = useRef<HTMLDivElement>(null);
	const monitor = useLatestProcessRequestExecutionStateMonitor();
	const showUpdateSuccess = marketStateDailySnapshotState.calendarDay.isCurrentMonth &&
		isMarketStateCalendarProcess(monitor.latestCompletedExecutionState);
	const isUpdating = marketStateDailySnapshotState.calendarDay.isCurrentMonth &&
		monitor.isInteractionLocked &&
		isMarketStateCalendarProcess(monitor.latestActiveExecutionState);

	useEffect(() => {
		if (!!div.current) {
			if (selectedCalendarDay?.display === marketStateDailySnapshotState.calendarDay.display) {
				div.current.focus();
			}
		}
	}, [selectedCalendarDay]);

	return <div ref={div} tabIndex={index + 1}
		className={Utilities.classNames(' lg:min-h-[132px] hover:bg-gray-50 cursor-pointer hover:shadow-lg focus:outline-none focus:border-sky-300 focus:ring-1 focus:ring-sky-300', (marketStateDailySnapshotState.calendarDay.isPreviousMonth || marketStateDailySnapshotState.calendarDay.isNextMonth || marketStateDailySnapshotState.calendarDay.isPast) ? 'relative bg-gray-50 text-gray-500' : 'relative bg-white')}>
		<div className="flex justify-between px-3 lg:py-4">
			<time dateTime={marketStateDailySnapshotState.calendarDay.display}
						className={Utilities.classNames('sm:ml-auto md:ml-0', marketStateDailySnapshotState.calendarDay.isToday ? 'flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 font-semibold text-white' : '')}>{marketStateDailySnapshotState.calendarDay.index.day}</time>
			<div className="flex relative top-1">
				<div
					className={Utilities.classNames('absolute right-0 top-0 text-green-300 opacity-0 transition-opacity ease-in-out delay-150 duration-500', showUpdateSuccess ? 'opacity-100' : '')}>
					<CheckCircleIcon className="h-5 w-5" />
				</div>
				{isUpdating && <Spinner />}
			</div>
		</div>
		{marketStateDailySnapshotState.calendarDay.isCurrentMonth && !marketStateDailySnapshotState.calendarDay.isPast && <>
				<span className="md:text-sm sm:text-xs text-center block mt-2 md:mb-5 text-gray-300 italic font-light">Not Available</span>
		</>}
		{marketStateDailySnapshotState.calendarDay.isPast && <>
			<span className="text-sm text-center block mt-2 md:mb-5 text-gray-300 italic font-light">--</span>
		</>}
	</div>;
};

const isMarketStateCalendarProcess = (processRequestExecutionState: { process: Process } | null | undefined) => {
	return processRequestExecutionState?.process === Process.COMPANY_HOTEL_MARKET_STATE ||
		processRequestExecutionState?.process === Process.COMPANY_HOTEL_MARKET_STATE_OTA ||
		processRequestExecutionState?.process === Process.PRICE_RECOMMENDATION;
};

export default EmptyCalendarDayFragment;
